import React, { useState, useEffect, useCallback } from 'react';
import {
  Table, Button, Input, Modal, Form, Select, Switch,
  Tag, Space, Tooltip, Popconfirm, Typography, message,
  Row, Col, Card, Badge
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  UserOutlined, ReloadOutlined, ExclamationCircleOutlined,
  LockOutlined
} from '@ant-design/icons';
import { usuariosService } from '../services/api';
import { useAuth } from '../context/AuthContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;

const ROL_COLORS = { root: 'red', admin: 'blue', gerencia: 'gold', rh: 'green', vista: 'default' };
const ROL_LABELS = { root: 'Root', admin: 'Admin', gerencia: 'Gerencia', rh: 'RH', vista: 'Vista' };

function Usuarios() {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await usuariosService.getAll();
      setUsuarios(res.data.data || []);
    } catch (error) {
      message.error(error.displayMessage || 'Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsuarios();
  }, [fetchUsuarios]);

  const openCreateModal = () => {
    setEditingUser(null);
    form.resetFields();
    form.setFieldsValue({ activo: true, rol: 'vista' });
    setModalOpen(true);
  };

  const openEditModal = (record) => {
    setEditingUser(record);
    form.setFieldsValue({
      nombre: record.nombre,
      rol: record.rol,
      activo: Boolean(record.activo)
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (editingUser) {
        await usuariosService.update(editingUser.id, values);
        message.success('Usuario actualizado exitosamente');
      } else {
        await usuariosService.create(values);
        message.success('Usuario creado exitosamente');
      }

      setModalOpen(false);
      fetchUsuarios();
    } catch (error) {
      if (error.errorFields) return;
      message.error(error.displayMessage || 'Error al guardar usuario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await usuariosService.delete(record.id);
      message.success(`Usuario "${record.usuario}" eliminado exitosamente`);
      fetchUsuarios();
    } catch (error) {
      message.error(error.displayMessage || 'Error al eliminar usuario');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
      align: 'center',
      render: (id) => <Text type="secondary" style={{ fontSize: 12 }}>#{id}</Text>
    },
    {
      title: 'Usuario',
      dataIndex: 'usuario',
      key: 'usuario',
      render: (usuario) => (
        <Space>
          <UserOutlined style={{ color: '#1677ff' }} />
          <Text code>{usuario}</Text>
        </Space>
      )
    },
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (nombre) => <Text strong>{nombre}</Text>
    },
    {
      title: 'Rol',
      dataIndex: 'rol',
      key: 'rol',
      width: 100,
      render: (rol) => <Tag color={ROL_COLORS[rol]}>{ROL_LABELS[rol]}</Tag>
    },
    {
      title: 'Estado',
      dataIndex: 'activo',
      key: 'activo',
      width: 90,
      align: 'center',
      render: (activo) => activo
        ? <Badge status="success" text="Activo" />
        : <Badge status="default" text="Inactivo" />
    },
    {
      title: 'Creado',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (val) => (
        <Text type="secondary" style={{ fontSize: 12 }}>
          {val ? dayjs(val).format('DD/MM/YYYY HH:mm') : '—'}
        </Text>
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 110,
      fixed: 'right',
      render: (_, record) => {
        const isSelf = record.id === currentUser?.id;
        return (
          <Space size={4}>
            <Tooltip title="Editar usuario">
              <Button
                type="primary"
                ghost
                size="small"
                icon={<EditOutlined />}
                onClick={() => openEditModal(record)}
              />
            </Tooltip>
            <Tooltip title={isSelf ? 'No puedes eliminarte a ti mismo' : 'Eliminar usuario'}>
              <span>
                <Popconfirm
                  title="¿Eliminar usuario?"
                  description={`El usuario "${record.usuario}" será eliminado permanentemente.`}
                  onConfirm={() => handleDelete(record)}
                  okText="Sí, eliminar"
                  cancelText="Cancelar"
                  icon={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                  okButtonProps={{ danger: true }}
                  disabled={isSelf}
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    disabled={isSelf}
                  />
                </Popconfirm>
              </span>
            </Tooltip>
          </Space>
        );
      }
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Usuarios</Title>
          <Text type="secondary">Gestión de usuarios y roles del sistema</Text>
        </div>
      </div>

      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col>
            <Button icon={<ReloadOutlined />} onClick={fetchUsuarios}>
              Actualizar
            </Button>
          </Col>
          <Col flex="auto" style={{ textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              Nuevo Usuario
            </Button>
          </Col>
        </Row>
      </Card>

      <Card bordered={false}>
        <Table
          dataSource={usuarios}
          columns={columns}
          rowKey="id"
          loading={loading}
          size="small"
          scroll={{ x: 700 }}
          pagination={{
            pageSize: 15,
            showSizeChanger: false,
            showTotal: (total) => `${total} usuario(s)`,
          }}
          locale={{ emptyText: 'No hay usuarios registrados' }}
        />
      </Card>

      <Modal
        title={editingUser ? `Editar Usuario: ${editingUser.usuario}` : 'Nuevo Usuario'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText={editingUser ? 'Guardar Cambios' : 'Crear Usuario'}
        cancelText="Cancelar"
        confirmLoading={saving}
        width={480}
        destroyOnClose
      >
        <Form form={form} layout="vertical" requiredMark="optional" style={{ marginTop: 16 }}>
          {!editingUser && (
            <Form.Item
              name="usuario"
              label="Nombre de Usuario"
              rules={[
                { required: true, message: 'El usuario es requerido' },
                { min: 3, message: 'Mínimo 3 caracteres' },
                { max: 50, message: 'Máximo 50 caracteres' },
                { pattern: /^[a-z0-9_]+$/, message: 'Solo letras minúsculas, números y guión bajo' }
              ]}
            >
              <Input
                prefix={<UserOutlined />}
                placeholder="ej: juan_perez"
                maxLength={50}
                onChange={(e) => form.setFieldValue('usuario', e.target.value.toLowerCase())}
              />
            </Form.Item>
          )}

          <Form.Item
            name="nombre"
            label="Nombre Completo"
            rules={[
              { required: true, message: 'El nombre es requerido' },
              { max: 100, message: 'Máximo 100 caracteres' }
            ]}
          >
            <Input placeholder="Nombre completo del usuario" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="password"
            label={editingUser ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña'}
            rules={[
              ...(!editingUser ? [{ required: true, message: 'La contraseña es requerida' }] : []),
              { min: 6, message: 'Mínimo 6 caracteres' }
            ]}
          >
            <Input.Password
              prefix={<LockOutlined />}
              placeholder={editingUser ? 'Dejar vacío para mantener actual' : 'Mínimo 6 caracteres'}
            />
          </Form.Item>

          <Form.Item
            name="rol"
            label="Rol"
            rules={[{ required: true, message: 'El rol es requerido' }]}
          >
            <Select placeholder="Selecciona un rol">
              <Option value="root">
                <Tag color="red">Root</Tag> — Acceso total + administración
              </Option>
              <Option value="admin">
                <Tag color="blue">Admin</Tag> — Crear y editar (sin eliminar)
              </Option>
              <Option value="gerencia">
                <Tag color="gold">Gerencia</Tag> — Igual que Admin + autorizar órdenes de compra
              </Option>
              <Option value="rh">
                <Tag color="green">RH</Tag> — Recursos Humanos
              </Option>
              <Option value="vista">
                <Tag color="default">Vista</Tag> — Solo lectura
              </Option>
            </Select>
          </Form.Item>

          <Form.Item name="activo" label="Estado" valuePropName="checked">
            <Switch checkedChildren="Activo" unCheckedChildren="Inactivo" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default Usuarios;
